import type { PageMedia } from "../../types";
import { pageContentWebUrlSchema } from "../../utils/validatePageContent";

export interface LibraryMedia extends PageMedia {
  filename?: string;
}

export const MEDIA_PAGE_SIZE = 12;
export const IMAGE_UPLOAD_TYPES = "image/jpeg,image/png,image/webp,image/svg+xml,image/gif";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export function readLibraryMedia(value: unknown): LibraryMedia | null {
  if (
    !isRecord(value) ||
    typeof value.id !== "string" ||
    !value.id ||
    (typeof value.mimeType === "string" && !value.mimeType.startsWith("image/"))
  ) {
    return null;
  }
  // Legacy upload filenames contain spaces. Encode those on read without
  // changing stored names or relaxing protocol, credential, or control checks.
  const url = pageContentWebUrlSchema.safeParse(
    typeof value.url === "string" ? value.url.replaceAll(" ", "%20") : value.url
  );
  if (!url.success) return null;
  return {
    id: value.id,
    url: url.data,
    alt: typeof value.alt === "string" ? value.alt : "",
    ...(typeof value.filename === "string" && { filename: value.filename }),
    ...(typeof value.width === "number" && { width: value.width }),
    ...(typeof value.height === "number" && { height: value.height }),
  };
}

export function mediaLibraryUrl(search: string, page: number): string {
  const query = new URLSearchParams({
    depth: "0",
    limit: String(MEDIA_PAGE_SIZE),
    page: String(page),
    sort: "-createdAt",
    "where[and][0][mimeType][contains]": "image/",
  });
  const term = search.trim();
  if (term) {
    query.set("where[and][1][or][0][alt][contains]", term);
    query.set("where[and][1][or][1][filename][contains]", term);
  }
  return `/api/payload/media?${query}`;
}

export function mediaResponseError(result: unknown, fallback: string): string {
  if (isRecord(result) && Array.isArray(result.errors)) {
    const first = result.errors[0];
    if (isRecord(first) && typeof first.message === "string") return first.message;
  }
  return fallback;
}

export function readMediaPage(result: unknown): {
  docs: LibraryMedia[];
  hasNextPage: boolean;
} | null {
  if (!isRecord(result) || !Array.isArray(result.docs)) return null;
  return {
    docs: result.docs.flatMap((item) => {
      const image = readLibraryMedia(item);
      return image ? [image] : [];
    }),
    hasNextPage: result.hasNextPage === true,
  };
}

export function uploadedMedia(result: unknown): LibraryMedia | null {
  return isRecord(result) ? readLibraryMedia(result.doc) : null;
}

export function mergeMedia<T extends PageMedia>(current: T[], additions: T[]): T[] {
  return [...new Map([...current, ...additions].map((item) => [item.id, item])).values()];
}
